import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  People,
  AttachMoney,
  Assessment,
  Download,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { membershipService } from '../../services/membershipService';
import { MembershipAnalyticsFilters } from '../../types/membership';

const MembershipAnalyticsTab: React.FC = () => {
  const [filters, setFilters] = useState<MembershipAnalyticsFilters>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0],
    planType: '',
    status: '',
  });

  // Fetch analytics data
  const { data: metricsData } = useQuery({
    queryKey: ['membershipMetrics', filters],
    queryFn: () => membershipService.getMembershipMetrics(filters),
  });

  const { data: planPerformanceData } = useQuery({
    queryKey: ['planPerformance', filters],
    queryFn: () => membershipService.getPlanPerformanceMetrics(filters),
  });

  const { data: usageData } = useQuery({
    queryKey: ['usageAnalytics', filters],
    queryFn: () => membershipService.getUsageAnalytics(filters),
  });

  const { data: revenueData } = useQuery({
    queryKey: ['revenueAnalytics', filters],
    queryFn: () => membershipService.getRevenueAnalytics(filters),
  });

  const { data: retentionData } = useQuery({
    queryKey: ['retentionAnalytics'],
    queryFn: () => membershipService.getCustomerRetentionAnalytics(),
  });

  const handleFilterChange = (field: keyof MembershipAnalyticsFilters) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleExportReport = async () => {
    try {
      const reportData = await membershipService.generateMembershipReport(filters);

      // Create and download CSV
      const csvContent = generateCSVReport((reportData as any).data || reportData);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `membership-report-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  const generateCSVReport = (data: any) => {
    const metrics = data.metrics;
    const plans = data.planPerformance;

    let csv = 'Membership Analytics Report\n\n';
    csv += 'Metrics\n';
    csv += 'Total Subscriptions,Active Subscriptions,Expired Subscriptions,Total Revenue,Churn Rate,Retention Rate\n';
    csv += `${metrics.totalSubscriptions},${metrics.activeSubscriptions},${metrics.expiredSubscriptions},${metrics.totalRevenue},${metrics.churnRate}%,${metrics.retentionRate}%\n\n`;

    csv += 'Plan Performance\n';
    csv += 'Plan Name,Plan Type,Total Subscriptions,Active Subscriptions,Total Revenue,Churn Rate\n';
    plans.forEach((plan: any) => {
      csv += `${plan.planName},${plan.planType},${plan.totalSubscriptions},${plan.activeSubscriptions},${plan.totalRevenue},${plan.churnRate}%\n`;
    });

    return csv;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const metrics = (metricsData as any)?.data || metricsData;
  const planPerformance: any[] = (planPerformanceData as any)?.data || planPerformanceData || [];
  const usage: any = (usageData as any)?.data || usageData;
  const revenue: any = (revenueData as any)?.data || revenueData;
  const retention: any = (retentionData as any)?.data || retentionData;

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Analytics Filters
        </Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange('startDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange('endDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Plan Type</InputLabel>
              <Select
                value={filters.planType}
                onChange={handleFilterChange('planType')}
                label="Plan Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="TIERED">Tiered</MenuItem>
                <MenuItem value="CREDIT_BASED">Credit Based</MenuItem>
                <MenuItem value="ALL_INCLUSIVE">All Inclusive</MenuItem>
                <MenuItem value="PREPAID">Prepaid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange('status')}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PAUSED">Paused</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                <MenuItem value="EXPIRED">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Download />}
              onClick={handleExportReport}
            >
              Export Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Key Metrics Cards */}
      {metrics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <People color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Subscriptions</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {metrics.totalSubscriptions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {metrics.activeSubscriptions} active
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Revenue</Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {formatCurrency(metrics.totalRevenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg: {formatCurrency(metrics.averageSubscriptionValue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">Retention Rate</Typography>
                </Box>
                <Typography variant="h4" color="info.main">
                  {formatPercentage(metrics.retentionRate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Churn: {formatPercentage(metrics.churnRate)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Assessment color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">Conversion Rate</Typography>
                </Box>
                <Typography variant="h4" color="warning.main">
                  {formatPercentage(metrics.conversionRate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active/Total ratio
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Plan Performance Table */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan Performance
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Subscriptions</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Churn Rate</TableCell>
                      <TableCell align="right">Rank</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {planPerformance.map((plan: any) => (
                      <TableRow key={plan.planId}>
                        <TableCell>{plan.planName}</TableCell>
                        <TableCell>
                          <Chip
                            label={plan.planType.replace('_', ' ')}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {plan.totalSubscriptions} ({plan.activeSubscriptions} active)
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(plan.totalRevenue)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPercentage(plan.churnRate)}
                        </TableCell>
                        <TableCell align="right">#{plan.popularityRank}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Most Used Services */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Most Used Services
              </Typography>
              {usage?.mostUsedServices?.slice(0, 5).map((service: any, index: number) => (
                <Box
                  key={service.serviceId}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: index < 4 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {service.serviceName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {service.usageCount} uses
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="primary">
                    {formatPercentage(service.percentage)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Breakdown */}
        {revenue && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue by Payment Mode
                </Typography>
                {Object.entries(revenue.revenueByPaymentMode).map(([mode, data]: [string, any]) => (
                  <Box
                    key={mode}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                    }}
                  >
                    <Typography variant="body2">{mode}</Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(data.revenue)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.paymentCount} payments ({formatPercentage(data.percentage)})
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Customer Retention Summary */}
        {retention && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Retention Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Customers
                    </Typography>
                    <Typography variant="h6">{retention.totalCustomers}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Active Members
                    </Typography>
                    <Typography variant="h6">{retention.activeMembers}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Membership Penetration
                    </Typography>
                    <Typography variant="h6">
                      {formatPercentage(retention.membershipPenetration)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Renewal Rate
                    </Typography>
                    <Typography variant="h6">
                      {formatPercentage(retention.renewalRate)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default MembershipAnalyticsTab;